import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { api, imageUrl } from "../lib/api";
import { SITE_URL } from "../lib/constants";
import { useSEO, buildBreadcrumbsJsonLd } from "../lib/seo";
import { useSiteTexts } from "../lib/siteTexts";
import { PAGE_TEXTS } from "../data/pageTexts";
import { beeldLabel, imgSet } from "../data/portfolio";
import { CrossLink, ImageMosaic, PortfolioHero, PublishPolicy } from "../components/portfolio/PortfolioHero";
import { ProjectsGrid } from "../components/portfolio/ProjectsGrid";
import { ProjectsEmpty } from "../components/portfolio/ProjectsEmpty";
import { AdviceEndCta } from "../components/info/AdviceBlocks";
import { QuoteButton } from "../components/home/CtaButtons";

const T = PAGE_TEXTS.projecten.blocks;
const CRUMBS = [{ label: "Home", href: "/" }, { label: "Projecten", href: "/projecten" }];

const collectionLd = (seo, items) => ({
  "@context": "https://schema.org", "@type": "CollectionPage", name: seo.title, description: seo.body, url: `${SITE_URL}/projecten`,
  ...(items?.length ? { mainEntity: { "@type": "ItemList", itemListElement: items.map((p, i) => ({ "@type": "ListItem", position: i + 1, url: `${SITE_URL}/projecten/${p.slug}`, name: p.title })) } } : {}),
});

const useList = (path, filter = (x) => x) => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    let on = true;
    api.get(path).then((r) => on && setItems(filter(r.data || []))).catch(() => on && setItems([]));
    return () => { on = false; };
  }, [path]); // eslint-disable-line react-hooks/exhaustive-deps
  return items;
};

export default function Projecten() {
  const t = useSiteTexts("projecten");
  const items = useList("/projects", (l) => l.filter((p) => p.hero_image?.path));
  const inspiration = useList("/inspiration") || [];
  const seo = t(T.seo);
  useSEO({ title: seo.title, description: seo.body, path: "/projecten", jsonLd: collectionLd(seo, items), breadcrumbs: buildBreadcrumbsJsonLd(CRUMBS) });
  const live = items?.length > 0;
  const hero = t(live ? T.hero_live : T.hero);
  const mosaic = live
    ? items.map((p) => ({ id: p.id, src: imageUrl(p.hero_image.path), srcSet: imgSet(p.hero_image.path) }))
    : inspiration.map((it) => ({ id: it.id, src: it.image.path, srcSet: imgSet(it.image.path), beeldtype: it.beeldtype }));

  return (
    <div data-testid="projecten-page">
      <PortfolioHero crumbs={CRUMBS} eyebrow="Projecten" title={hero.title} lead={hero.body}
        aside={items !== null && mosaic.length ? <ImageMosaic items={mosaic} badge={live ? null : beeldLabel} testid={live ? "projects-hero-mosaic" : "inspiration-hero-mosaic"} /> : null}>
        <div className="mt-7 flex flex-col gap-4 sm:flex-row sm:items-center">
          <QuoteButton location="projecten-hero" />
          {!live && <Link to="/projecten/inspiratie" className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-maas hover:underline" data-testid="projects-hero-inspiration-link">Bekijk inspiratie <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>}
        </div>
        <PublishPolicy />
      </PortfolioHero>
      {items === null ? (
        <div className="flex justify-center py-24" data-testid="projects-loading"><Loader2 className="h-6 w-6 animate-spin text-maas" /></div>
      ) : live ? (
        <>
          <ProjectsGrid items={items} />
          <CrossLink to="/projecten/inspiratie" text="Meer ideeën opdoen? Bekijk onze inspiratiegalerij." label="Naar de inspiratiegalerij" testid="projects-inspiration-link" />
        </>
      ) : <ProjectsEmpty block={t(T.empty)} inspiration={inspiration} />}
      <AdviceEndCta title="Wilt u ook nieuwe kozijnen voor uw woning?" location="projecten" />
    </div>
  );
}
